/* eslint-disable @typescript-eslint/no-explicit-any -- U2000Input* attend FormStateHandlers générique large */
import type { ChangeEvent, FocusEvent } from "react";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputCurrency from "../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputDecimal from "../../../../u2000Core/components/formControls/input/U2000InputDecimal";
import U2000Label from "../../../../u2000Core/components/formControls/label/U2000Label";
import U2000InvalidFeedback from "../../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback";
import type { TFunction } from "i18next";
import { NS } from "../../../U3106";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { U3106ExpenseTypeFormRow } from "../../../U3106Service";

interface U3106ExpenseTypeBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3106ExpenseTypeFormRow>;
  t: TFunction;
}

function U3106ExpenseTypeBody({ formStateHandlers, t }: U3106ExpenseTypeBodyProps) {
  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;

  const statusVal = data?.status ?? 1;
  const statusIsStandard = statusVal === 0 || statusVal === 1;
  const activeChecked = data?.status === 1;

  const errStatus = formStateHandlers.getErrors().status;
  const hasStatusError =
    (formStateHandlers.getTouched().status && errStatus !== undefined) ||
    (formStateHandlers.isSubmitted() && errStatus !== undefined);

  const setStatusFromCheckbox = (checked: boolean) => {
    const ev = { target: { id: "status" } } as ChangeEvent<HTMLInputElement>;
    formStateHandlers.onChange?.(ev, checked ? 1 : 0);
  };

  const blurStatus = () => {
    formStateHandlers.onBlur?.({ target: { id: "status" } } as FocusEvent<HTMLInputElement>);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1.5">
          <U2000Label htmlFor="code" label={t("lblCode", { ns: NS })} required />
          <U2000InputText
            hideLabel
            formStateHandlers={formStateHandlers as any}
            fieldName="code"
            label={t("lblCode", { ns: NS })}
            required
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <U2000Label htmlFor="description" label={t("lblDescription", { ns: NS })} required />
          <div className="flex flex-row items-center gap-4">
            <div className="min-w-0 flex-1">
              <U2000InputText
                hideLabel
                formStateHandlers={formStateHandlers as any}
                fieldName="description"
                label={t("lblDescription", { ns: NS })}
                required
              />
            </div>
            {/* Statut 0/1 : pas U2000CheckBox (booléen) — saisie alignée sur l’API */}
            <div className="flex shrink-0 items-center">
              <div className="relative flex items-center gap-3">
                <input
                  id="status"
                  type="checkbox"
                  checked={activeChecked}
                  disabled={readonly}
                  onChange={(e) => {
                    if (readonly) return;
                    setStatusFromCheckbox(e.target.checked);
                  }}
                  onBlur={blurStatus}
                  className="peer absolute left-0 top-1/2 h-5 w-5 -translate-y-1/2 cursor-pointer opacity-0"
                  aria-checked={activeChecked}
                  aria-invalid={hasStatusError}
                />
                <label
                  htmlFor="status"
                  className={`relative flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded border transition-all duration-200
                  ${
                    hasStatusError
                      ? "border-red-500 bg-red-50 hover:border-red-600"
                      : readonly
                        ? "cursor-not-allowed border-gray-200 bg-muted"
                        : activeChecked
                          ? "border-blue-500 bg-blue-500 hover:border-blue-600 hover:bg-blue-600"
                          : "border-gray-300 bg-white hover:border-gray-400 hover:bg-muted"
                  }
                  peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/20 peer-focus-visible:ring-offset-2
                `}
                >
                  {activeChecked && (
                    <svg
                      className={readonly ? "text-gray-400" : "text-white"}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      aria-hidden={true}
                    >
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </label>
                <label
                  htmlFor="status"
                  className={`cursor-pointer text-sm font-medium leading-relaxed whitespace-nowrap ${readonly ? "cursor-not-allowed text-gray-500" : "text-gray-900 hover:text-gray-700"}`}
                >
                  {t("lblStatusActive", { ns: NS })}
                </label>
              </div>
            </div>
          </div>
          {!statusIsStandard ? (
            <p className="max-w-48 text-xs text-gray-500">{t("lblStatusOther", { ns: NS, value: statusVal })}</p>
          ) : null}
          <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName="status" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <U2000InputCurrency
          formStateHandlers={formStateHandlers as any}
          fieldName="factor"
          label={t("lblFactor", { ns: NS })}
          required={false}
          decimalDigits={2}
          allowNegative={true}
        />
        <U2000InputDecimal
          formStateHandlers={formStateHandlers as any}
          fieldName="majorFactor"
          label={t("lblMajorFactor", { ns: NS })}
          required
          decimalDigits={2}
          allowNegative={false}
        />
      </div>
    </div>
  );
}

export default U3106ExpenseTypeBody;
